import React, { useRef, useState } from 'react';
import { Box, TextField, Button, Typography, useTheme, Card, CardContent } from '@mui/material';
import * as Yup from 'yup';
import { Field, Form, Formik, FormikProps } from 'formik';
import { emailRegexConstant, passwordRegexConstant, phoneRegexConstant } from '../../constants';
import { AlertType, APIResponse, FreeKeyObject } from '../../utilities/types';
import { getApiErrorMessage } from '../../utilities/helper';
import CustomAlert from '../../components/elements/custom-alert.element';
import { resetPasswordApi, sendOTP, verifyOTP } from '../../apis';
import CustomSwal from '../../components/elements/custom-swal.element';
import useNavigateHook from '../../hooks/navigate.hook';

interface resetFormikProps {
  username: string;
  otp: string;
  password: string;
  confirm_password: string;
}

const AdminResetPasswordPage: React.FC = () => {
  const theme = useTheme();
  const { doNavigate } = useNavigateHook();
  const formikRef = useRef<FormikProps<resetFormikProps> | null>(null);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [resetStep, setResetStep] = useState<number>(0);
  const [resetKey, setResetKey] = useState<string>('');
  const [showResendOtpLink, setShowResendOtpLink] = useState(false);

  const [alertMessage, showAlertMessage] = useState<{ message: string; type: AlertType }>({
    message: '',
    type: 'info',
  });

  const toggleAlertMessage = (message: string, type: AlertType) => {
    if (message === '') {
      return;
    }
    showAlertMessage({ message, type });
    setTimeout(() => {
      showAlertMessage({ message: '', type: 'info' });
    }, 4000);
  };

  const doSendOTP = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    setShowResendOtpLink(false);
    try {
      const response = await sendOTP(
        emailRegexConstant.test(values.username) ? 'email' : 'mobile',
        values.username
      );
      if (response.status) {
        toggleAlertMessage('If account does exist, then OTP has been sent', 'info');
        setResetStep(1);
        setTimeout(
          () => {
            setShowResendOtpLink(true);
          },
          2 * 60 * 1000
        );
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const doVerifyOTP = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const response = (await verifyOTP(
        emailRegexConstant.test(values.username) ? 'email' : 'mobile',
        values.username,
        values.otp,
        true
      )) as APIResponse<FreeKeyObject>;
      if (response.status) {
        setResetKey(response.data.reset_key);
        setResetStep(2);
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const changePassword = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const response = await resetPasswordApi({
        password: values.password,
        confirm_password: values.confirm_password,
        reset_key: resetKey,
      });
      if (response.status) {
        CustomSwal.fire({
          icon: 'success',
          title: 'Password reset successfully. You can now login with your new password.',
        });
        setResetStep(0);
        formikRef.current.resetForm();
        doNavigate('/admin/login');
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const onSubmit = async () => {
    if (resetStep === 0) {
      doSendOTP();
    } else if (resetStep === 1) {
      doVerifyOTP();
    } else if (resetStep === 2) {
      changePassword();
    }
  };

  const validationSchemaObject: { [key: string]: any } = {};
  if (resetStep === 0) {
    validationSchemaObject.username = Yup.string()
      .required('Email or phone number is required')
      .test('is-email-or-phone', 'Must be a valid email or 10-digit phone number', (value) => {
        if (!value) return false;
        return emailRegexConstant.test(value) || phoneRegexConstant.test(value);
      });
  } else if (resetStep === 1) {
    validationSchemaObject.otp = Yup.string().required('OTP is required');
  } else if (resetStep === 2) {
    validationSchemaObject.password = Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required')
      .matches(
        passwordRegexConstant,
        'Password must contain at least one letter, one number and one special character'
      );
    validationSchemaObject.confirm_password = Yup.string()
      .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
      .required('Confirm Password is required');
  }

  const validationSchema = Yup.object().shape(validationSchemaObject);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: { xs: '90%', sm: '500px' },
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          textAlign="center"
          fontWeight={600}
          fontSize="18px"
          letterSpacing="0.8px"
        >
          Forgot Password
        </Typography>
        <Formik
          innerRef={formikRef}
          initialValues={{
            username: '',
            otp: '',
            password: '',
            confirm_password: '',
          }}
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={() => {
            onSubmit();
          }}
        >
          {({ values, errors, touched, isValid, dirty, handleChange }) => (
            <Form>
              {resetStep === 0 && (
                <Field
                  as={TextField}
                  name="username"
                  label="Email or Phone *"
                  placeholder="Enter your email or phone number"
                  fullWidth
                  margin="normal"
                  onChange={handleChange}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                />
              )}

              {resetStep === 1 && (
                <>
                  <Field
                    as={TextField}
                    name="otp"
                    label="OTP *"
                    fullWidth
                    margin="normal"
                    value={values.otp}
                    onChange={handleChange}
                    error={touched.otp && Boolean(errors.otp)}
                    helperText={touched.otp && errors.otp}
                  />
                  {showResendOtpLink && (
                    <Typography
                      sx={{
                        m: 0,
                        textAlign: 'left',
                        letterSpacing: '0.5px',
                        fontSize: '10px !important',
                      }}
                    >
                      {`Didn't receieved OTP? `}
                      <Button
                        onClick={doSendOTP}
                        variant="text"
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: '10px',
                          letterSpacing: '0.5px',
                          textDecoration: 'underline',
                          verticalAlign: 'baseline',
                          p: 0,
                          minWidth: 'auto',
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      >
                        Resend again
                      </Button>
                    </Typography>
                  )}
                </>
              )}

              {resetStep === 2 && (
                <>
                  <Field
                    as={TextField}
                    name="password"
                    label="Password *"
                    type="password"
                    fullWidth
                    margin="normal"
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                  />

                  <Field
                    as={TextField}
                    name="confirm_password"
                    label="Confirm Password *"
                    type="text"
                    fullWidth
                    margin="normal"
                    value={values.confirm_password}
                    onChange={handleChange}
                    error={touched.confirm_password && Boolean(errors.confirm_password)}
                    helperText={touched.confirm_password && errors.confirm_password}
                  />
                </>
              )}

              {/* Submit Button */}
              {/* <p>{`resetStep: ${resetStep}, isValid: ${isValid}, dirty: ${dirty}`}</p>
              <p>{`values: ${JSON.stringify(values)}`}</p> */}
              <Button
                type="button"
                variant="contained"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  onSubmit();
                }}
                sx={{ mt: 2, bgcolor: 'green', '&:hover': { bgcolor: 'darkgreen' } }}
                disabled={!isValid || !dirty || isSubmitting}
              >
                Submit
              </Button>

              {alertMessage.message !== '' && (
                <Box sx={{ my: 1 }}>
                  <CustomAlert type={alertMessage.type}>{alertMessage.message}</CustomAlert>
                </Box>
              )}

              <Typography align="center" sx={{ mt: 3, fontSize: '12px' }}>
                Remember Login Details?{' '}
              </Typography>
              <Button
                onClick={() => doNavigate('/admin/login')}
                variant="text"
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  display: 'block',
                  mx: 'auto',
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                Click Here
              </Button>
              <Button
                variant="text"
                color="info"
                sx={{
                  mt: 2,
                  mx: 'auto',
                  display: 'block',
                  textAlign: 'center',
                  textDecoration: 'underline',
                }}
                onClick={() => doNavigate('/')}
              >
                Go To Website
              </Button>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};

export default AdminResetPasswordPage;
